import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

/** Coloured category tile with a folder glyph (colour derived from the id). */
export function CategoryTile({
  bg,
  fg,
  size = 42,
  radius = 12,
  iconSize = 20,
}: {
  bg: string;
  fg: string;
  size?: number;
  radius?: number;
  iconSize?: number;
}) {
  return (
    <span
      className="flex shrink-0 items-center justify-center"
      style={{ width: size, height: size, borderRadius: radius, background: bg, color: fg }}
    >
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      </svg>
    </span>
  );
}

/** Catalog-share progress bar. */
export function ShareBar({
  pct,
  color,
  className = "",
}: {
  pct: number;
  color: string;
  className?: string;
}) {
  return (
    <span
      className={[
        "block h-[5px] overflow-hidden rounded-full bg-[color:var(--track)]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span
        className="block h-full rounded-full"
        style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color }}
      />
    </span>
  );
}

/**
 * The ⋯ actions menu shared by the grid card and table row. Edit / Delete are
 * both wired to the existing category mutations.
 */
export function CategoryActionsMenu({
  onEdit,
  onDelete,
  disabled = false,
}: {
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
}) {
  const t = useTranslations();
  return (
    <Menu as="div" className="relative inline-flex">
      <MenuButton
        className="flex h-10 w-10 items-center justify-center rounded-[9px] border border-[color:var(--card-br)] bg-[color:var(--card)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--hover)] lg:h-[30px] lg:w-[30px]"
        aria-label={t(keys.categories.moreActions)}
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
          <circle cx="12" cy="5" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </svg>
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-50 mt-1 min-w-[168px] rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--card)] p-1 shadow-[0_12px_28px_rgba(0,0,0,0.16)] focus:outline-none"
      >
        <MenuItem>
          <button
            type="button"
            onClick={onEdit}
            className="flex w-full items-center gap-2 rounded-[8px] px-3 py-2 text-start text-[13px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)] data-[focus]:bg-[color:var(--hover)]"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
            {t(keys.common.edit)}
          </button>
        </MenuItem>
        <MenuItem>
          <button
            type="button"
            onClick={onDelete}
            className="flex w-full items-center gap-2 rounded-[8px] px-3 py-2 text-start text-[13px] font-medium text-[color:var(--error-500)] transition-colors hover:bg-[color:var(--error-100)] data-[focus]:bg-[color:var(--error-100)]"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
            </svg>
            {t(keys.common.delete)}
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
}
